/**
 * CredentialIconGrid Component
 * Visual icon grid for selecting credential types when creating a new credential
 */

import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CredentialIcon } from './CredentialIcon';
import {
  CREDENTIAL_CATEGORIES,
  getAllCredentialDefinitions,
  getCredentialsByCategory,
  searchCredentials,
  getCredentialCategoryKeys,
  type CredentialCategory,
  type CredentialDefinition,
} from '@/utils/credentialDefinitions';

interface CredentialIconGridProps {
  /** Callback when a credential type is selected */
  onSelect: (credentialType: string) => void;

  /** Optional filter to only show specific credential types */
  allowedTypes?: string[];

  /** Initial category to display */
  initialCategory?: CredentialCategory;
}

/**
 * Icon grid for selecting credential types
 *
 * @example
 * <CredentialIconGrid
 *   onSelect={(type) => console.log('Selected:', type)}
 *   initialCategory="databases"
 * />
 */
export const CredentialIconGrid: React.FC<CredentialIconGridProps> = ({
  onSelect,
  allowedTypes,
  initialCategory,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CredentialCategory | 'all'>(
    initialCategory || 'all'
  );

  // Get all available credentials, filtered by allowedTypes if provided
  const allCredentials = useMemo(() => {
    const credentials = getAllCredentialDefinitions();
    if (allowedTypes && allowedTypes.length > 0) {
      return credentials.filter((c) => allowedTypes.includes(c.type));
    }
    return credentials;
  }, [allowedTypes]);

  // Filter credentials based on search and category
  const filteredCredentials = useMemo(() => {
    let result = allCredentials;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.label.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          c.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      result = result.filter((c) => c.category === selectedCategory);
    }

    // Sort alphabetically by label
    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [allCredentials, searchQuery, selectedCategory]);

  // Get categories that have credentials (for sidebar)
  const availableCategories = useMemo(() => {
    const categoryCounts = new Map<CredentialCategory, number>();

    allCredentials.forEach((c) => {
      const count = categoryCounts.get(c.category) || 0;
      categoryCounts.set(c.category, count + 1);
    });

    return getCredentialCategoryKeys().filter(
      (cat) => (categoryCounts.get(cat) || 0) > 0
    );
  }, [allCredentials]);

  // Don't show category filter if filtering by allowedTypes and only one category
  const showCategoryFilter = !allowedTypes || availableCategories.length > 1;

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filter Header */}
      <div className="flex-none pb-4">
        <div className="flex gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search credentials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Category Dropdown */}
          {showCategoryFilter && (
            <Select
              value={selectedCategory}
              onValueChange={(value) => setSelectedCategory(value as CredentialCategory | 'all')}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All ({allCredentials.length})
                </SelectItem>
                {availableCategories.map((cat) => {
                  const categoryDef = CREDENTIAL_CATEGORIES[cat];
                  const count = allCredentials.filter((c) => c.category === cat).length;
                  return (
                    <SelectItem key={cat} value={cat}>
                      {categoryDef.label} ({count})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Credential Grid */}
      <div className="flex-1 overflow-hidden min-h-0">
        <ScrollArea className="h-full">
          {filteredCredentials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground">
                No credentials found matching your search
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 pb-4 pr-4">
              {filteredCredentials.map((credential) => (
                <CredentialGridItem
                  key={credential.type}
                  credential={credential}
                  onClick={() => onSelect(credential.type)}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

// ============================================================================
// Grid Item Component
// ============================================================================

interface CredentialGridItemProps {
  credential: CredentialDefinition;
  onClick: () => void;
}

const CredentialGridItem: React.FC<CredentialGridItemProps> = ({
  credential,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-2 p-4 rounded-lg border border-border',
        'bg-muted/30 hover:bg-muted/60 hover:border-primary/50',
        'transition-all duration-150 cursor-pointer group',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
      )}
    >
      <CredentialIcon type={credential.type} size="lg" showBackground />
      <div className="text-center">
        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1">
          {credential.label}
        </p>
      </div>
    </button>
  );
};

export default CredentialIconGrid;
